'use client';

import React, { memo, useMemo } from 'react';
import Image from 'next/image';
import styles from './HalfCarouselHero.module.scss';
import { Group } from '@mantine/core';

export type CarouselHeroImage = {
    url: string;
    alt: string;
};

export type HeroCTA = {
    label: string;
    href: string;
};

export type HeroTitle =
    | string
    | {
          before?: string;
          highlight: string;
          after?: string;
          lines?: string[];
      };

export type HalfCarouselHeroOverlayVariant = 'card' | 'onImage';
export type HalfCarouselHeroBottomShape = 'line' | 'wave';

export type HalfCarouselHeroSlice = {
    type?: 'half-carousel-hero';

    imageUrl?: string;
    imageAlt?: string;
    images?: CarouselHeroImage[];

    height?: string;

    intervalMs?: number;
    fadeMs?: number;

    eyebrow?: string;
    title?: HeroTitle;
    subtitle?: string;
    subtitleLines?: string[];

    cta?: HeroCTA;
    cta2?: HeroCTA;

    underline?: boolean;

    overlayVariant?: HalfCarouselHeroOverlayVariant;
    bottomShape?: HalfCarouselHeroBottomShape;

    newsTitle?: string;
    newsItems?: Array<{
        date: string;
        label: string;
        href: string;
    }>;

    parallaxPx?: number;
};

type Props = {
    data: HalfCarouselHeroSlice;
    className?: string;
};

function HalfCarouselHeroComponent({ data, className }: Props) {
    const images = useMemo<CarouselHeroImage[]>(() => {
        if (Array.isArray(data.images) && data.images.length > 0) {
            return data.images.filter((image) => Boolean(image?.url));
        }

        if (data.imageUrl) {
            return [
                {
                    url: data.imageUrl,
                    alt: data.imageAlt ?? 'Hero image',
                },
            ];
        }

        return [];
    }, [data.images, data.imageUrl, data.imageAlt]);

    /**
     * Config
     */
    const fadeMs = data.fadeMs ?? 700;

    const computedHeight = data.height?.trim() || '100dvh';

    const overlayVariant: HalfCarouselHeroOverlayVariant =
        data.overlayVariant ?? 'card';

    const bottomShape: HalfCarouselHeroBottomShape = data.bottomShape ?? 'line';

    const underline = data.underline ?? false;

    const hasOverlay =
        !!data.eyebrow ||
        !!data.title ||
        !!data.subtitle ||
        !!data.subtitleLines?.length ||
        !!data.cta ||
        !!data.cta2;

    const heroImage = images[0];

    if (!heroImage) {
        return null;
    }

    const rootClasses = [
        styles.root,
        overlayVariant === 'onImage' ? styles.variantOnImage : styles.variantCard,
        bottomShape === 'wave' ? styles.bottomWave : styles.bottomLine,
        className,
    ]
        .filter(Boolean)
        .join(' ');

    const shouldPrioritizeCurrentImage = true;

    const renderTitle = () => {
        if (!data.title) return null;

        if (typeof data.title === 'string') {
            return data.title;
        }

        if (Array.isArray(data.title.lines) && data.title.lines.length > 0) {
            return data.title.lines.map((line, index) => (
                <span key={`${line}-${index}`} className={styles.titleLine}>
                    {line}
                </span>
            ));
        }

        return (
            <>
                {data.title.before && <span>{data.title.before} </span>}

                {underline ? (
                    <span className={styles.underlineWord}>
                        {data.title.highlight}
                    </span>
                ) : (
                    <span className={styles.highlightWord}>
                        {data.title.highlight}
                    </span>
                )}

                {data.title.after && <span> {data.title.after}</span>}
            </>
        );
    };

    /**
     * Subtitle renderer
     */
    const renderSubtitle = () => {
        if (
            Array.isArray(data.subtitleLines) &&
            data.subtitleLines.length > 0
        ) {
            return data.subtitleLines.map((line, index) => (
                <span key={`${line}-${index}`} className={styles.subtitleLine}>
                    {line}
                </span>
            ));
        }

        return data.subtitle ?? null;
    };

    return (
        <section
            className={rootClasses}
            style={
                {
                    height: computedHeight,
                    ['--fade-ms' as never]: `${fadeMs}ms`,
                } as React.CSSProperties
            }
            aria-label="Hero image"
        >
            <div className={styles.viewport}>
                <div className={[styles.layer, styles.currentLayer].join(' ')}>
                    <Image
                        src={heroImage.url}
                        alt={heroImage.alt}
                        fill
                        sizes="(max-width: 768px) 100vw, 50vw"
                        quality={70}
                        className={styles.image}
                        priority={shouldPrioritizeCurrentImage}
                        loading={shouldPrioritizeCurrentImage ? 'eager' : 'lazy'}
                        fetchPriority={
                            shouldPrioritizeCurrentImage ? 'high' : 'auto'
                        }
                    />
                </div>

                <div
                    className={[
                        styles.scrim,
                        overlayVariant === 'onImage'
                            ? styles.scrimStrong
                            : styles.scrimSoft,
                    ].join(' ')}
                    aria-hidden="true"
                />
            </div>

            {hasOverlay && (
                <div
                    className={[
                        styles.textWrap,
                        overlayVariant === 'onImage'
                            ? styles.textOnImage
                            : styles.textCard,
                    ].join(' ')}
                >
                    {data.eyebrow && (
                        <div className={styles.eyebrow}>{data.eyebrow}</div>
                    )}

                    {data.title && <h1 className={styles.title}>{renderTitle()}</h1>}

                    {(data.subtitle || data.subtitleLines?.length) && (
                        <p className={styles.subtitle}>{renderSubtitle()}</p>
                    )}

                    <Group
                        justify="center"
                        wrap="wrap"
                        className={styles.ctaGroup}
                    >
                        {data.cta?.label && data.cta?.href && (
                            <a className={styles.cta} href={data.cta.href}>
                                {data.cta.label}
                            </a>
                        )}

                        {data.cta2?.label && data.cta2?.href && (
                            <a className={styles.cta} href={data.cta2.href}>
                                {data.cta2.label}
                            </a>
                        )}
                    </Group>
                </div>
            )}

        </section>
    );
}

export default memo(HalfCarouselHeroComponent);
